import React, { useState, useEffect } from 'react';
import { 
  X, 
  Search, 
  Send,
  Briefcase,
  Building,
  MapPin,
  DollarSign,
  Calendar,
  ChevronDown,
  Check
} from 'lucide-react';

const SubmitToJobModal = ({ isOpen, onClose, candidate, onSubmit }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [submissionData, setSubmissionData] = useState({
    status: 'sourced',
    source: 'Manual',
    payRate: '',
    billRate: '',
    salary: '',
    comments: ''
  });

  const submissionStatuses = [
    'sourced', 'screened', 'submitted', 'client_reviewing', 'client_accepted', 
    'client_rejected', 'interview_scheduled', 'interview_scheduled', 'interview_scheduled',
    'interview_passed', 'interview_failed', 'offer_made', 'offer_accepted', 
    'offer_rejected', 'placed', 'started', 'rejected', 'withdrawn'
  ];

  const sources = [
    'Manual', 'LinkedIn', 'Indeed', 'Monster', 'Dice', 'CareerBuilder', 'Glassdoor',
    'Company Website', 'Referral', 'Direct', 'Recruiter Network', 'Job Board'
  ];

  useEffect(() => {
    if (isOpen) {
      fetchJobs();
    }
  }, [isOpen]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs`);
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => 
    job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.id.toString().includes(searchTerm) ||
    job.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async () => {
    if (!selectedJob || !candidate) {
      alert('Please select a job to submit this candidate to.');
      return;
    }

    const finalSubmissionData = {
      jobId: selectedJob.id,
      candidateId: candidate.id,
      addedBy: 'Current User', // Would come from auth context
      submission_date: new Date().toISOString(),
      recruiter_id: 'current-user-id', // Would come from auth context
      recruiter_name: 'Current User', // Would come from auth context
      candidate_name: `${candidate.first_name} ${candidate.last_name}`,
      job_title: selectedJob.title,
      ...submissionData
    };

    try {
      await onSubmit(finalSubmissionData);
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error submitting candidate:', error);
    }
  };

  const resetForm = () => {
    setSelectedJob(null);
    setSearchTerm('');
    setSubmissionData({
      status: 'sourced',
      source: 'Manual',
      payRate: '',
      billRate: '',
      salary: '',
      comments: ''
    });
  };

  if (!isOpen || !candidate) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Submit Candidate to Job</h2>
              <p className="text-gray-600 mt-1">
                Submit <span className="font-medium">{candidate.first_name} {candidate.last_name}</span> to a job position
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Job Selection */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Job Position</h3>
                
                {/* Search Jobs */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search jobs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Jobs List */}
                <div className="space-y-2 max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                  {loading ? (
                    <div className="p-6 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-gray-600">Loading jobs...</p>
                    </div>
                  ) : filteredJobs.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      No jobs found matching your search
                    </div>
                  ) : (
                    filteredJobs.map(job => (
                      <div
                        key={job.id}
                        onClick={() => setSelectedJob(job)}
                        className={`p-4 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                          selectedJob?.id === job.id ? 'bg-blue-50 border-blue-300' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <Briefcase className="w-4 h-4 text-gray-500" />
                              <h4 className="font-semibold text-gray-900">{job.title}</h4>
                              {selectedJob?.id === job.id && (
                                <Check className="w-4 h-4 text-blue-600" />
                              )}
                            </div>
                            <div className="mt-2 space-y-1 text-sm text-gray-600">
                              <div className="flex items-center space-x-2">
                                <Building className="w-3 h-3" />
                                <span>{job.department}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <MapPin className="w-3 h-3" />
                                <span>{job.location}</span>
                              </div>
                              {job.salary_range && (
                                <div className="flex items-center space-x-2">
                                  <DollarSign className="w-3 h-3" />
                                  <span>${job.salary_range.min?.toLocaleString()} - ${job.salary_range.max?.toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={`px-2 py-1 text-xs rounded-full ${
                            job.status === 'open' ? 'bg-green-100 text-green-800' :
                            job.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {job.status}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Submission Details */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Submission Details</h3>
                
                <div className="space-y-4">
                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={submissionData.status}
                      onChange={(e) => setSubmissionData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {submissionStatuses.map(status => (
                        <option key={status} value={status}>
                          {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Source */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                    <select
                      value={submissionData.source}
                      onChange={(e) => setSubmissionData(prev => ({ ...prev, source: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {sources.map(source => (
                        <option key={source} value={source}>{source}</option>
                      ))}
                    </select>
                  </div>

                  {/* Pay Rate */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pay Rate ($/hour)</label>
                    <input
                      type="number"
                      placeholder="Enter hourly rate"
                      value={submissionData.payRate}
                      onChange={(e) => setSubmissionData(prev => ({ ...prev, payRate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Bill Rate */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bill Rate ($/hour)</label>
                    <input
                      type="number"
                      placeholder="Enter bill rate"
                      value={submissionData.billRate}
                      onChange={(e) => setSubmissionData(prev => ({ ...prev, billRate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Salary */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Annual Salary ($)</label>
                    <input
                      type="number"
                      placeholder="Enter annual salary"
                      value={submissionData.salary}
                      onChange={(e) => setSubmissionData(prev => ({ ...prev, salary: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Comments */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
                    <textarea
                      placeholder="Add notes about this submission..."
                      rows={3}
                      value={submissionData.comments}
                      onChange={(e) => setSubmissionData(prev => ({ ...prev, comments: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Selected Job Summary */}
              {selectedJob && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Selected Job</h4>
                  <div className="text-sm text-blue-800">
                    <div className="font-medium">{selectedJob.title}</div>
                    <div>{selectedJob.department} • {selectedJob.location}</div>
                    <div className="text-blue-600 mt-1">ID: {selectedJob.id}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedJob ? `Ready to submit ${candidate.first_name} ${candidate.last_name} to ${selectedJob.title}` : 'Select a job position to continue'}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!selectedJob}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Send className="w-4 h-4" />
                <span>Submit Candidate</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitToJobModal;